import { WriteRestrictedAlert } from "@jugl-web/domain-resources/entities/components/WriteRestrictedAlert";
import { produceIsModuleAvailableFn } from "@jugl-web/domain-resources/entities/utils/produceIsModuleAvailableFn";
import {
  EntityModel,
  SubscriptionInfoModel,
  SubscriptionPlanModuleId,
  useRestApiProvider,
  UserRole,
} from "@jugl-web/rest-api";
import { TasksApiTag } from "@jugl-web/rest-api/tasks";
import { PageLoaderFull } from "@jugl-web/ui-components/cross-platform";
import { ApiUTCDateTime, isValidUUID } from "@jugl-web/utils";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { tasksApi } from "@web-src/features/api/createApi";
import {
  FC,
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useDispatch } from "react-redux";
import { useMatch, useNavigate } from "react-router-dom";
import { ENTITY_ID_KEY, saveItemToStorage } from "@jugl-web/utils/storage";
import { useEntitiesListProvider } from "@web-src/features/entities/providers/EntitiesListProvider/useEntitiesListProvider";
import { EntitiesListProvider } from "@web-src/features/entities/providers/EntitiesListProvider/EntitiesListProvider";
import { useAnonymousSession } from "@web-src/modules/auth/providers/AnonymousSessionProvider";
import {
  PlanLimitsReachedAlert,
  PlanLimitsReachedAlertProps,
} from "../../components/PlanLimitsReachedAlert";
import { ModuleNotAvailableAlert } from "./components/ModuleNotAvailableAlert";
import { EntityContext, EntityContextValue } from "./EntityContext";
import { SubscriptionAlertType } from "./types/SubscriptionAlertType";

const noop = () => {};

const createAnonymousEntityContextValue = (
  entityId: string
): EntityContextValue => {
  const now = new Date().toISOString() as ApiUTCDateTime;

  const mockEntity: EntityModel = {
    id: entityId,
    entityRelId: entityId,
    name: "",
    currency: "USD",
    bannerImg: null,
    logoImg: null,
    unread: false,
    type: "cx",
    info: {},
    role: UserRole.member,
    country: "",
    desc: "",
    subscription: null,
    subscriptionInfo: { type: "free" },
    billingEmail: null,
    isEmailVerified: false,
    uniqueId: entityId,
    userCount: 0,
    __dto: {
      id: entityId,
      entity_rel_id: entityId,
      billing_email: "",
      is_email_verified: false,
      auth_mode: null,
      country: "",
      currency: "USD",
      desc: "",
      banner_img: null,
      display_pic: null,
      info: {},
      name: "",
      parent_id: null,
      role: UserRole.member,
      unique_id: entityId,
      status: "active" as never,
      task_count: 0,
      type: "org",
      chat_unread: 0,
      unread: false,
      total_unread: 0,
      user_count: 0,
      missed_call: 0,
      subscription: null,
      entity_attachments: [],
      inserted_at: now,
      updated_at: now,
    },
  };

  const mockSubscriptionInfo: SubscriptionInfoModel = {
    entityId,
    customerAddress: null,
    customerName: "",
    customerNotes: "",
    customerTaxId: "",
    isTrial: false,
    status: "free",
    validFrom: now,
    planTill: now,
    graceTill: now,
    isInGrace: false,
    isInPlan: false,
    isInTrial: false,
    seatsPlanDefault: 0,
    seatsUtilized: 0,
    seatsAvailable: 0,
    planIsActive: true,
    modules: null,
    planId: null,
    balance: 0,
    actionsAllowed: true,
    socketAllowed: false,
    isDealMirror: false,
  };

  return {
    isInitialized: true,
    isInitializationError: false,
    subscriptionInfo: mockSubscriptionInfo,
    entity: mockEntity,
    entityId,
    role: UserRole.member,
    isVerifyBillingAlertVisible: false,
    reinitialize: async () => {},
    subscriptionRequiredAction: (func) => func,
    showWriteRestrictedAlert: noop,
    entitiesList: [mockEntity],
    isEntitiesFetching: false,
    verifyBillingEmail: noop,
    refetchSubscriptionInfo: noop,
    moduleRequiredAction: (_moduleId, availableCallback) =>
      availableCallback?.(),
    isModuleAvailable: () => true,
    showPlanLimitsReachedAlert: noop,
  };
};

const EntityProviderContent: FC<PropsWithChildren> = ({ children }) => {
  const navigate = useNavigate();

  // TODO: error handling
  const entityIdMatch = useMatch("/:entityId/*");
  // const entityVerifyEmailPageMatch = useMatch("/:entityId/verify-email");

  const { hasSession } = useAnonymousSession();

  const validEntityId = useMemo(
    () =>
      hasSession &&
      entityIdMatch?.params.entityId &&
      isValidUUID(entityIdMatch.params.entityId)
        ? entityIdMatch.params.entityId
        : null,
    [entityIdMatch?.params.entityId, hasSession]
  );
  const { entitiesApi } = useRestApiProvider();

  const [visibleAlert, setVisibleAlert] = useState<
    SubscriptionAlertType | undefined
  >();

  const dispatch = useDispatch();

  const resetApiState = useCallback(() => {
    // TODO: Consider using `emptySplitApi.util.resetApiState()` to reset all API states (needs more testing)
    const resetTaskQueriesAction = tasksApi.util.invalidateTags([
      TasksApiTag.task,
      TasksApiTag.taskActivity,
    ]);

    dispatch(resetTaskQueriesAction);
  }, [dispatch]);

  useEffect(() => {
    setVisibleAlert(undefined);

    if (validEntityId) {
      saveItemToStorage(ENTITY_ID_KEY, validEntityId);
      resetApiState();
    }
  }, [resetApiState, validEntityId]);

  const [verifyEmail, { isLoading: isVerifyEmailLoading }] =
    entitiesApi.useVerifyEmailMutation();

  const verifyBillingEmail = useCallback(
    async (entityId: string, billingEmail: string, otp?: string) => {
      if (!billingEmail) {
        return null;
      }
      const response = await verifyEmail({
        entityId,
        data: { billing_email: billingEmail, otp },
      });
      return response;
    },
    [verifyEmail]
  );

  const {
    refetchEntities: refetch,
    entitiesState: {
      isLoading: isEntitiesLoading,
      isFetching: isEntitiesFetching,
      isError: isEntitiesError,
      entities: entitiesList,
    },
  } = useEntitiesListProvider();

  const {
    data: subscriptionInfoData,
    isLoading: isSubscriptionInfoLoading,
    isError: isSubscriptionInfoError,
    refetch: refetchSubscriptionInfo,
  } = entitiesApi.useGetSubscriptionInfoQuery(
    validEntityId
      ? {
          entityId: validEntityId,
        }
      : skipToken,
    { refetchOnMountOrArgChange: true }
  );

  const subscriptionInfo =
    subscriptionInfoData?.entityId === validEntityId
      ? subscriptionInfoData
      : undefined;

  const entity = useMemo(
    () =>
      validEntityId
        ? entitiesList?.find((item) => item.id === validEntityId) || null
        : null,
    [entitiesList, validEntityId]
  );

  const reinitialize = useCallback(async () => {
    refetch();
  }, [refetch]);

  const showWriteRestrictedAlert = useCallback(() => {
    setVisibleAlert("actionNotAllowed");
  }, []);

  const subscriptionRequiredAction: EntityContextValue["subscriptionRequiredAction"] =
    useCallback(
      (func) => {
        if (subscriptionInfo?.actionsAllowed) {
          return func;
        }
        return () => showWriteRestrictedAlert();
      },
      [showWriteRestrictedAlert, subscriptionInfo?.actionsAllowed]
    );

  const [verifyAlertShown, setVerifyAlertShown] = useState<{
    [key: string]: boolean;
  }>({});
  const [isVerifyBillingAlertVisible, setIsVerifyBillingAlertVisible] =
    useState<boolean>(false);
  useEffect(() => {
    if (
      !entity ||
      entity.role !== UserRole.admin ||
      localStorage.getItem("jugl:updateBillingPopupShown") ||
      !entity.billingEmail ||
      entity.isEmailVerified ||
      verifyAlertShown[entity.id]
    ) {
      return;
    }
    localStorage.setItem("jugl:updateBillingPopupShown", "true");
    setVerifyAlertShown((prev) => ({ ...prev, [entity.id]: true }));
    setIsVerifyBillingAlertVisible(true);
  }, [entity, verifyAlertShown, isVerifyBillingAlertVisible]);

  const [moduleNotAvailableAlertState, setModuleNotAvailableAlertState] =
    useState<{
      isOpen: boolean;
      moduleId: SubscriptionPlanModuleId | `${SubscriptionPlanModuleId}`;
    }>();

  const isModuleAvailable = useMemo(
    () => produceIsModuleAvailableFn(subscriptionInfo, entity?.id),
    [subscriptionInfo, entity?.id]
  );

  const moduleRequiredAction: EntityContextValue["moduleRequiredAction"] =
    useCallback(
      (moduleId, availableCallback, unavailableCallback) => {
        const isAvailable = isModuleAvailable(moduleId);
        if (isAvailable) {
          availableCallback?.();
        } else {
          setModuleNotAvailableAlertState({ isOpen: true, moduleId });
          unavailableCallback?.();
        }
      },
      [isModuleAvailable]
    );

  const [planLimitsReachedAlertState, setPlanLimitsReachedAlertState] =
    useState<PlanLimitsReachedAlertProps["type"]>();

  const showPlanLimitsReachedAlert = useCallback(
    (type: PlanLimitsReachedAlertProps["type"]) => {
      setPlanLimitsReachedAlertState(type);
    },
    []
  );

  const value: EntityContextValue = useMemo(
    () => ({
      isInitialized:
        !isEntitiesLoading && !isSubscriptionInfoLoading && !!subscriptionInfo,
      isInitializationError: isEntitiesError || isSubscriptionInfoError,
      subscriptionInfo: subscriptionInfo || null,
      entity: entity || null,
      entityId: entity?.id || null,
      role: entity?.role || undefined,
      isVerifyBillingAlertVisible,
      reinitialize,
      subscriptionRequiredAction,
      showWriteRestrictedAlert,
      entitiesList: entitiesList || [],
      isEntitiesFetching,
      verifyBillingEmail,
      refetchSubscriptionInfo,
      moduleRequiredAction,
      isModuleAvailable,
      showPlanLimitsReachedAlert,
    }),
    [
      isEntitiesLoading,
      isVerifyBillingAlertVisible,
      isSubscriptionInfoLoading,
      isEntitiesError,
      isSubscriptionInfoError,
      subscriptionInfo,
      entity,
      reinitialize,
      subscriptionRequiredAction,
      showWriteRestrictedAlert,
      entitiesList,
      isEntitiesFetching,
      verifyBillingEmail,
      refetchSubscriptionInfo,
      moduleRequiredAction,
      isModuleAvailable,
      showPlanLimitsReachedAlert,
    ]
  );

  return (
    <EntityContext.Provider value={value}>
      <>
        <PageLoaderFull isActive={isVerifyEmailLoading} isTransparent />
        {entity && (
          <>
            {moduleNotAvailableAlertState && (
              <ModuleNotAvailableAlert
                entityId={entity.id}
                isAdmin={entity.role === UserRole.admin}
                isOpen={moduleNotAvailableAlertState.isOpen}
                module={moduleNotAvailableAlertState.moduleId}
                onRequestClose={() => {
                  setModuleNotAvailableAlertState((prev) =>
                    prev
                      ? {
                          ...prev,
                          isOpen: false,
                        }
                      : undefined
                  );
                }}
              />
            )}
            <PlanLimitsReachedAlert
              isOpen={!!planLimitsReachedAlertState}
              isAdmin={entity.role === UserRole.admin}
              onRequestClose={() => setPlanLimitsReachedAlertState(undefined)}
              type={planLimitsReachedAlertState}
            />
            <WriteRestrictedAlert
              isOpen={visibleAlert === "actionNotAllowed"}
              isAdmin={entity.role === UserRole.admin}
              onRequestClose={() => setVisibleAlert(undefined)}
              onRequestPlanUpgrade={() => {
                navigate(`/${entity.id}/workspace/subscription/manage`);
                setVisibleAlert(undefined);
              }}
            />
            {/* <Alert
              isOpen={
                !isEntitiesLoading &&
                !isEntitiesError &&
                !isEntitiesFetching &&
                subscriptionInfo &&
                subscriptionInfo.actionsAllowed &&
                isVerifyBillingAlertVisible
              }
              title="Verify email"
              content={
                <>
                  Indicated Workspace Email will receive all Workspace updates,
                  we want to verify it, to make sure your{" "}
                  <span className="font-semibold text-blue-500">
                    data is secured
                  </span>{" "}
                  🤝
                </>
              }
              onRequestClose={() => setIsVerifyBillingAlertVisible(false)}
              buttons={[
                {
                  text: "Later",
                  onClick: (e, { closeAlert }) => {
                    if (entityVerifyEmailPageMatch) {
                      navigate(`/${entity.id}/chats`);
                    }
                    closeAlert();
                  },
                  color: "grey",
                },
                {
                  text: "Verify Now",
                  onClick: async (e, { closeAlert }) => {
                    if (!entity.billingEmail) {
                      return;
                    }
                    await verifyBillingEmail(entity.id, entity.billingEmail);
                    navigate(`/${entity.id}/verify-email`);
                    closeAlert();
                  },
                },
              ]}
            /> */}
          </>
        )}
        {children}
      </>
    </EntityContext.Provider>
  );
};

export const EntityProvider: FC<PropsWithChildren> = ({ children }) => {
  const { isAnonymous, entityId: anonymousEntityId } = useAnonymousSession();

  if (isAnonymous && anonymousEntityId) {
    const value = createAnonymousEntityContextValue(anonymousEntityId);
    return (
      <EntitiesListProvider>
        <EntityContext.Provider value={value}>
          {children}
        </EntityContext.Provider>
      </EntitiesListProvider>
    );
  }

  return (
    <EntitiesListProvider>
      <EntityProviderContent>{children}</EntityProviderContent>
    </EntitiesListProvider>
  );
};
